import { useState, useEffect } from 'react';
import { getMfaStatus, setupMfa, confirmMfaSetup, disableMfa } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, ShieldOff, Mail, MessageSquare, Phone, Check } from 'lucide-react';

const METHODS = [
  { id: 'email', label: 'Email', desc: 'Receive codes via email', icon: Mail, available: true },
  { id: 'sms', label: 'SMS', desc: 'Receive codes via text message', icon: MessageSquare, available: true },
  { id: 'phone', label: 'Phone Call', desc: 'Receive codes via phone call', icon: Phone, available: true },
];

export default function MfaSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [phone, setPhone] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('select');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await getMfaStatus();
      setStatus(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleSetup = async () => {
    if ((selectedMethod === 'sms' || selectedMethod === 'phone') && !phone.trim()) {
      toast.error('Phone number is required'); return;
    }
    setActionLoading(true);
    try {
      const res = await setupMfa({ method: selectedMethod, phone: phone.trim() });
      setChallengeId(res.data.challenge_id);
      setStep('verify');
      toast.success(`Verification code sent via ${selectedMethod}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    }
    setActionLoading(false);
  };

  const handleConfirm = async () => {
    if (!code.trim() || code.trim().length !== 6) {
      toast.error('Enter the 6-digit code'); return;
    }
    setActionLoading(true);
    try {
      await confirmMfaSetup({ challenge_id: challengeId, code: code.trim() });
      toast.success('MFA enabled!');
      setSetupMode(false); setStep('select'); setCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    }
    setActionLoading(false);
  };

  const handleDisable = async () => {
    setActionLoading(true);
    try {
      await disableMfa({ password: confirmPassword });
      toast.success('MFA disabled');
      setDisableMode(false); setConfirmPassword('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable MFA');
    }
    setActionLoading(false);
  };

  if (loading) return <div className="text-sm font-body" style={{ color: '#9CA3AF' }}>Loading MFA settings...</div>;

  return (
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }} data-testid="mfa-settings">
      <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB' }}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={18} style={{ color: '#D98A4A' }} strokeWidth={1.8} />
          <div>
            <h3 className="font-heading text-lg font-medium">Two-Factor Authentication</h3>
            <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>Add an extra layer of security to your account</p>
          </div>
        </div>
        {status?.mfa_enabled ? (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-semibold" style={{ background: 'rgba(45,106,79,0.1)', color: '#2D7A5D' }}>
            <Check size={12} /> Enabled
          </span>
        ) : (
          <span className="text-xs font-body font-semibold" style={{ color: '#9CA3AF' }}>Disabled</span>
        )}
      </div>

      <div className="p-6">
        {status?.mfa_enabled && !disableMode && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(45,106,79,0.04)', border: '1px solid rgba(45,106,79,0.15)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(45,106,79,0.1)' }}>
                {status.mfa_method === 'sms' ? <MessageSquare size={18} style={{ color: '#2D7A5D' }} /> :
                 status.mfa_method === 'phone' ? <Phone size={18} style={{ color: '#2D7A5D' }} /> :
                 <Mail size={18} style={{ color: '#2D7A5D' }} />}
              </div>
              <div>
                <p className="text-sm font-body font-medium" style={{ color: '#1A1A1A' }}>
                  {status.mfa_method === 'email' ? 'Email' : status.mfa_method === 'sms' ? 'SMS' : 'Phone Call'} verification active
                </p>
                <p className="text-xs font-body" style={{ color: '#4B5563' }}>
                  {status.mfa_phone ? `Phone: ${status.mfa_phone}` : 'Codes sent to your email address'}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setDisableMode(true)} className="rounded-full font-body text-sm" style={{ borderColor: '#E5E3DB', color: '#0A3622' }} data-testid="mfa-disable-start-btn">
              <ShieldOff size={14} className="mr-2" /> Disable MFA
            </Button>
          </div>
        )}

        {status?.mfa_enabled && disableMode && (
          <div className="space-y-4">
            <p className="text-sm font-body" style={{ color: '#4B5563' }}>Enter your password to disable MFA:</p>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Your password" className="h-11 rounded-lg font-body" style={{ borderColor: '#E5E3DB' }} data-testid="mfa-disable-password" />
            <div className="flex gap-3">
              <Button onClick={handleDisable} disabled={actionLoading} className="rounded-full font-body text-sm text-white" style={{ background: '#0A3622' }} data-testid="mfa-disable-confirm-btn">
                {actionLoading ? 'Disabling...' : 'Confirm Disable'}
              </Button>
              <Button variant="outline" onClick={() => { setDisableMode(false); setConfirmPassword(''); }} className="rounded-full font-body text-sm" style={{ borderColor: '#E5E3DB' }}>Cancel</Button>
            </div>
          </div>
        )}

        {!status?.mfa_enabled && !setupMode && (
          <div className="space-y-4">
            <p className="text-sm font-body" style={{ color: '#4B5563' }}>
              Protect your account by requiring a verification code in addition to your password when signing in.
            </p>
            <Button onClick={() => setSetupMode(true)} className="rounded-full font-body font-medium text-white" style={{ background: '#0A3622' }} data-testid="mfa-enable-btn">
              <ShieldCheck size={16} className="mr-2" /> Enable MFA
            </Button>
          </div>
        )}

        {!status?.mfa_enabled && setupMode && step === 'select' && (
          <div className="space-y-5">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold" style={{ color: '#9CA3AF' }}>Choose verification method</p>
            <div className="space-y-2">
              {METHODS.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelectedMethod(m.id)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200"
                    style={{
                      border: selectedMethod === m.id ? '2px solid #D98A4A' : '1px solid #E5E3DB',
                      background: selectedMethod === m.id ? 'rgba(198,156,109,0.04)' : '#FFFFFF',
                    }}
                    data-testid={`mfa-method-${m.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: selectedMethod === m.id ? 'rgba(198,156,109,0.1)' : '#F7F6F2' }}>
                      <Icon size={18} style={{ color: selectedMethod === m.id ? '#D98A4A' : '#9CA3AF' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-body font-medium" style={{ color: '#1A1A1A' }}>{m.label}</p>
                      <p className="text-xs font-body" style={{ color: '#4B5563' }}>{m.desc}</p>
                    </div>
                    {selectedMethod === m.id && <Check size={16} style={{ color: '#D98A4A' }} />}
                  </button>
                );
              })}
            </div>

            {(selectedMethod === 'sms' || selectedMethod === 'phone') && (
              <div className="space-y-2">
                <Label className="text-xs font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#4B5563' }}>Phone Number</Label>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="h-11 rounded-lg font-body" style={{ borderColor: '#E5E3DB' }} data-testid="mfa-phone-input" />
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={handleSetup} disabled={actionLoading} className="rounded-full font-body font-medium text-white" style={{ background: '#0A3622' }} data-testid="mfa-setup-send-btn">
                {actionLoading ? 'Sending...' : 'Send Verification Code'}
              </Button>
              <Button variant="outline" onClick={() => setSetupMode(false)} className="rounded-full font-body text-sm" style={{ borderColor: '#E5E3DB' }}>Cancel</Button>
            </div>
          </div>
        )}

        {!status?.mfa_enabled && setupMode && step === 'verify' && (
          <div className="space-y-5">
            <p className="text-sm font-body" style={{ color: '#4B5563' }}>Enter the 6-digit verification code:</p>
            <Input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="h-12 rounded-lg font-mono text-lg text-center tracking-[0.5em]" style={{ borderColor: '#E5E3DB' }} data-testid="mfa-setup-code-input" />
            <div className="flex gap-3">
              <Button onClick={handleConfirm} disabled={actionLoading || code.length !== 6} className="rounded-full font-body font-medium text-white" style={{ background: code.length === 6 ? '#C84B31' : '#9CA3AF' }} data-testid="mfa-setup-confirm-btn">
                {actionLoading ? 'Verifying...' : 'Confirm & Enable'}
              </Button>
              <Button variant="outline" onClick={() => { setStep('select'); setCode(''); }} className="rounded-full font-body text-sm" style={{ borderColor: '#E5E3DB' }}>Back</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
